import React, { useCallback, useEffect, useState } from 'react';
import { BillingPolicyReturn, PaymentReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage } from '../utils/billingHelpers';
import PaymentCard from './PaymentCard';
import RecordPaymentDialog from './RecordPaymentDialog';
import {
  AssessFeeDialog,
  RefundPaymentDialog,
  ReturnPaymentDialog,
} from './PaymentActionDialogs';

interface Props {
  hoaId: string;
  homeId: string;
  canRecord: boolean;
  canRefund: boolean;
  canReturn: boolean;
}

const PER_PAGE = 25;

/** This home's payments, with what each one paid. */
const HomePaymentList: React.FC<Props> = ({
  hoaId,
  homeId,
  canRecord,
  canRefund,
  canReturn,
}) => {
  const [payments, setPayments] = useState<PaymentReturn[]>([]);
  const [policy, setPolicy] = useState<BillingPolicyReturn | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [returning, setReturning] = useState<PaymentReturn | null>(null);
  const [refunding, setRefunding] = useState<PaymentReturn | null>(null);
  const [assessing, setAssessing] = useState<PaymentReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, pol] = await Promise.all([
        billingService.listHomePayments({ homeId, page, perPage: PER_PAGE }),
        billingService.getHoaPolicy({ hoaId }),
      ]);
      setPayments(list.data.payments);
      setTotal(list.data.total);
      setPolicy(pol.data);
    } catch (err) {
      console.error('Error loading home payments:', err);
      setError(
        billingErrorMessage(err, "Couldn't load this home’s payments.")
      );
    } finally {
      setLoading(false);
    }
  }, [hoaId, homeId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  if (loading && payments.length === 0) return <LoadingSpinner />;

  return (
    <>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load payments"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Payments</Subheading>
          <Text className="mt-1">
            What this home has paid, and which charges each payment cleared.
          </Text>
        </div>
        {canRecord && (
          <Button color="sky" onClick={() => setRecording(true)}>
            Record a payment
          </Button>
        )}
      </div>

      {payments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>This home has not paid anything yet.</Text>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {payments.map((p) => (
              <PaymentCard
                key={p.id}
                payment={p}
                allowsCreditBalances={policy?.allow_credit_balances ?? false}
                onReturn={canReturn ? setReturning : undefined}
                onRefund={canRefund ? setRefunding : undefined}
                onAssessFee={canReturn ? setAssessing : undefined}
              />
            ))}
          </div>
          {lastPage > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <Text>
                {total} payments · page {page} of {lastPage}
              </Text>
              <div className="flex gap-2">
                <Button
                  plain
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  plain
                  disabled={page >= lastPage}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <RecordPaymentDialog
        isOpen={recording}
        onClose={() => setRecording(false)}
        onRecorded={load}
        hoaId={hoaId}
        homes={[]}
        fixedHomeId={homeId}
      />
      <ReturnPaymentDialog
        payment={returning}
        nsfFee={policy?.nsf_fee}
        onClose={() => setReturning(null)}
        onDone={load}
      />
      <RefundPaymentDialog
        payment={refunding}
        onClose={() => setRefunding(null)}
        onDone={load}
      />
      <AssessFeeDialog
        payment={assessing}
        nsfFee={policy?.nsf_fee}
        onClose={() => setAssessing(null)}
        onDone={load}
      />
    </>
  );
};

export default HomePaymentList;
